/** XeAgents.ai**/
import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Calendar as CalendarIcon, Clock, Settings, ChevronLeft, ChevronRight, AlertCircle, CalendarDays, CalendarCheck, User, CheckCircle2, XCircle, Loader2, Users, Mail, Link2, Trash2, Copy, UserPlus, Shield, RefreshCw } from "lucide-react";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, startOfWeek, endOfWeek } from "date-fns";
import { formatDate } from "@/lib/date-format";

interface AppointmentMetadata {
  aiCollectedPhone?: string;
  verifiedPhone?: string;
  phoneDiscrepancy?: boolean;
  aiCollectedName?: string;
  verifiedName?: string;
  calcomBookingId?: number | string;
  calcomBookingUid?: string;
  meetingUrl?: string;
  hostName?: string;
  hostEmail?: string;
  source?: string;
}

type CalendarProvider = 'microsoft' | 'google';

interface CalendarProviderSettings {
  provider: CalendarProvider;
  // Microsoft settings
  microsoftConfigured: boolean;
  microsoftUserId?: string; // The configured user email or Object ID
  // Google settings
  googleConfigured: boolean;
  googleUserId?: string; // The configured Google Calendar email
  googleClientId?: string;
}

interface GoogleCalendarStatus {
  connected: boolean;
  email?: string;
  calendarId?: string;
  isShared?: boolean;
  message?: string;
  lastUsedAt?: string;
  lastError?: string;
}

interface TeamMember {
  id: string;
  userId: string;
  email: string;
  name: string | null;
  role: string;
  status: 'pending' | 'connected' | 'expired';
  inviteToken: string | null;
  inviteExpiresAt: string | null;
  calendarTokenId: string | null;
  provider: string | null;
  calendarEmail: string | null;
  usesAdminCalendar: boolean;
  lastSyncAt: string | null;
  invitedAt: string;
  connectedAt: string | null;
}

interface ApprovedDomain {
  id: string;
  userId: string;
  domain: string;
  provider: string;
  adminEmail: string;
  isActive: boolean;
  approvedAt: string;
  approvedBy: string | null;
}

// Domain approval status for current user (team member flow)
interface MyDomainApproval {
  approved: boolean;
  domain: string | null;
  provider?: string;
  adminEmail?: string;
  approvedAt?: string;
  message?: string;
}

interface Appointment {
  id: string;
  userId: string;
  callId: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string | null;
  scheduledFor: string;
  duration: number;
  serviceName: string | null;
  status: string;
  notes: string | null;
  metadata: AppointmentMetadata | null;
  googleMeetLink?: string | null;
  teamsLink?: string | null;
  createdAt: string;
}

interface AppointmentSettings {
  id: string;
  userId: string;
  allowOverlap: boolean;
  bufferTime: number;
  maxPerDay: number | null;
  workingHoursStart: string;
  workingHoursEnd: string;
  workingDays: string[];
  createdAt: string;
  updatedAt: string;
}

const defaultSettings: Partial<AppointmentSettings> = {
  allowOverlap: false,
  bufferTime: 15,
  maxPerDay: null,
  workingHoursStart: "09:00",
  workingHoursEnd: "17:00",
  workingDays: ["monday", "tuesday", "wednesday", "thursday", "friday"],
};

export default function AppointmentsPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [view, setView] = useState<"day" | "week" | "month">("month");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [settingsData, setSettingsData] = useState<Partial<AppointmentSettings>>(defaultSettings);
  const [settingsTab, setSettingsTab] = useState<"general" | "calendar" | "team">("general");

  // Team management state
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [usesAdminCalendar, setUsesAdminCalendar] = useState(false);
  const [copiedInviteLink, setCopiedInviteLink] = useState<string | null>(null);

  // Admin choice state for new users
  const [isOrgAdmin, setIsOrgAdmin] = useState<boolean | null>(null);
  const [manualHostEmail, setManualHostEmail] = useState("");
  const [manualHostName, setManualHostName] = useState("");

  // Calendar provider selection
  const [calendarProvider, setCalendarProvider] = useState<CalendarProvider>('google');

  // Microsoft Outlook settings state
  const [microsoftCalendarUserId, setMicrosoftCalendarUserId] = useState("");
  const [microsoftTestResult, setMicrosoftTestResult] = useState<{ success: boolean; message: string; slots?: string[] } | null>(null);

  // Google Calendar settings state
  const [googleCalendarUserId, setGoogleCalendarUserId] = useState("");
  const [googleTestResult, setGoogleTestResult] = useState<{ success: boolean; message: string; slots?: string[] } | null>(null);

  const { data: appointments = [], isLoading } = useQuery<Appointment[]>({
    queryKey: ["/api/flow-automation/appointments"],
  });

  const { data: settings } = useQuery<AppointmentSettings>({
    queryKey: ["/api/flow-automation/appointment-settings"],
  });

  const { data: calendarProviderSettings } = useQuery<CalendarProviderSettings>({
    queryKey: ["/api/flow-automation/calendar-provider-settings"],
  });

  // Google Calendar connection status
  const { data: googleCalendarStatus, refetch: refetchGoogleStatus } = useQuery<GoogleCalendarStatus>({
    queryKey: ["/api/flow-automation/google-calendar/status"],
    enabled: calendarProvider === 'google',
  });

  // Microsoft Calendar connection status
  const { data: microsoftCalendarStatus, refetch: refetchMicrosoftStatus } = useQuery<GoogleCalendarStatus>({
    queryKey: ["/api/flow-automation/microsoft-calendar/status"],
    enabled: calendarProvider === 'microsoft',
  });

  // Team members query
  const { data: teamMembers = [], refetch: refetchTeamMembers } = useQuery<TeamMember[]>({
    queryKey: ["/api/flow-automation/calendar-team-members"],
    enabled: settingsTab === 'team',
  });

  // Approved domains query (for admin view)
  const { data: approvedDomains = [], refetch: refetchApprovedDomains } = useQuery<ApprovedDomain[]>({
    queryKey: ["/api/flow-automation/approved-calendar-domains"],
    enabled: settingsTab === 'team',
  });

  // Check if current user's domain is approved (for team member flow)
  const { data: myDomainApproval, isLoading: isLoadingDomainApproval } = useQuery<MyDomainApproval>({
    queryKey: ["/api/flow-automation/check-my-domain-approval"],
  });

  // Fetch existing manual host email (if configured)
  interface ManualHostData {
    configured: boolean;
    hostEmail: string | null;
    hostName: string | null;
    status?: string;
    connectedAt?: string;
  }
  const { data: manualHostData, refetch: refetchManualHost } = useQuery<ManualHostData>({
    queryKey: ["/api/flow-automation/calendar-team-members/my-host"],
  });

  // The Cal.com account (owner of the configured API key) that actually checks
  // availability and creates bookings - distinct from the "host email" above,
  // which is only who meeting invites get sent to.
  interface CalComConnectedAccount {
    connected: boolean;
    email?: string;
    username?: string;
  }
  const { data: calcomConnectedAccount } = useQuery<CalComConnectedAccount>({
    queryKey: ["/api/flow-automation/calcom-settings/connected-account"],
  });

  // Determine if user is an admin (has their own calendar connected) or a team member
  const isCalendarAdmin = googleCalendarStatus?.connected || microsoftCalendarStatus?.connected;

  // Check if user has manual host email configured (no calendar integration)
  const hasManualHostEmail = manualHostData?.configured && manualHostData?.hostEmail;

  // Whether appointments can actually be booked for this user — via a connected
  // Google/Microsoft calendar, a connected Cal.com account, or a manual host email.
  // The calendar grid and appointment list below should show for all of these, not
  // just full OAuth admins, otherwise Cal.com-only / manual-host users never see
  // their own bookings.
  const hasBookingSetup = isCalendarAdmin || calcomConnectedAccount?.connected || hasManualHostEmail;

  // Auto-show host email section if user already has one configured
  useEffect(() => {
    if (hasManualHostEmail && isOrgAdmin === null) {
      setIsOrgAdmin(false); // Show the non-admin section with saved host email
    }
  }, [hasManualHostEmail, isOrgAdmin]);

  useEffect(() => {
    if (settings) {
      setSettingsData(settings);
    }
  }, [settings]);

  useEffect(() => {
    if (calendarProviderSettings) {
      setCalendarProvider(calendarProviderSettings.provider || 'google');
      setMicrosoftCalendarUserId(calendarProviderSettings.microsoftUserId || '');
      setGoogleCalendarUserId(calendarProviderSettings.googleUserId || '');
    }
  }, [calendarProviderSettings]);

  // Handle Google OAuth callback URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const googleConnected = urlParams.get('google_connected');
    const googleError = urlParams.get('google_error');
    const microsoftConnected = urlParams.get('microsoft_connected');
    const microsoftError = urlParams.get('microsoft_error');

    if (googleConnected === 'true') {
      toast({
        title: "Google Calendar Connected",
        description: "Your Google Calendar has been connected successfully!",
      });
      // Refresh status and clean URL
      queryClient.invalidateQueries({ queryKey: ["/api/flow-automation/google-calendar/status"] });
      queryClient.invalidateQueries({ queryKey: ["/api/flow-automation/calendar-provider-settings"] });
      window.history.replaceState({}, '', window.location.pathname);
      // Open settings dialog to the calendar tab
      setSettingsTab('calendar');
      setSettingsOpen(true);
      setCalendarProvider('google');
    } else if (googleError) {
      toast({
        title: "Google Calendar Connection Failed",
        description: decodeURIComponent(googleError),
        variant: "destructive",
      });
      window.history.replaceState({}, '', window.location.pathname);
    } else if (microsoftConnected === 'true') {
      toast({
        title: "Microsoft Calendar Connected",
        description: "Your Microsoft Calendar has been connected successfully!",
      });
      // Refresh status and clean URL
      queryClient.invalidateQueries({ queryKey: ["/api/flow-automation/microsoft-calendar/status"] });
      queryClient.invalidateQueries({ queryKey: ["/api/flow-automation/calendar-provider-settings"] });
      window.history.replaceState({}, '', window.location.pathname);
      // Open settings dialog to the calendar tab
      setSettingsTab('calendar');
      setSettingsOpen(true);
      setCalendarProvider('microsoft');
    } else if (microsoftError) {
      toast({
        title: "Microsoft Calendar Connection Failed",
        description: decodeURIComponent(microsoftError),
        variant: "destructive",
      });
      window.history.replaceState({}, '', window.location.pathname);
    }

    // Handle team member calendar connection callbacks
    const teamMemberConnected = urlParams.get('team_member_connected');
    const teamMemberError = urlParams.get('team_member_error');

    if (teamMemberConnected === 'true') {
      toast({
        title: "Calendar Connected",
        description: "Team member calendar has been connected successfully!",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/flow-automation/calendar-team-members"] });
      window.history.replaceState({}, '', window.location.pathname);
      // Open settings dialog to the team tab
      setSettingsTab('team');
      setSettingsOpen(true);
    } else if (teamMemberError) {
      toast({
        title: "Calendar Connection Failed",
        description: decodeURIComponent(teamMemberError),
        variant: "destructive",
      });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [toast]);

  const saveSettingsMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        allowOverlap: settingsData.allowOverlap ?? false,
        bufferTime: settingsData.bufferTime ?? 15,
        maxPerDay: settingsData.maxPerDay ?? null,
        workingHoursStart: settingsData.workingHoursStart || "09:00",
        workingHoursEnd: settingsData.workingHoursEnd || "17:00",
        workingDays: settingsData.workingDays || ["monday", "tuesday", "wednesday", "thursday", "friday"],
      };
      
      const res = await apiRequest("PUT", "/api/flow-automation/appointment-settings", payload);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/flow-automation/appointment-settings"] });
      toast({ title: t("appointments.toast.settingsSaved") });
      setSettingsOpen(false);
    },
    onError: (error: any) => {
      toast({
        title: t("appointments.toast.settingsFailed"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const saveCalendarProviderMutation = useMutation({
    mutationFn: async (provider: CalendarProvider) => {
      const res = await apiRequest("PUT", "/api/flow-automation/calendar-provider-settings", { provider });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/flow-automation/calendar-provider-settings"] });
      toast({
        title: "Calendar Provider Updated",
        description: `Switched to ${calendarProvider === 'microsoft' ? 'Microsoft Outlook' : 'Google Calendar'} for availability checking.`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to Update Calendar Provider",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const saveMicrosoftCalendarUserMutation = useMutation({
    mutationFn: async (userEmail: string) => {
      const res = await apiRequest("PUT", "/api/flow-automation/microsoft-calendar-user", { userEmail });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/flow-automation/calendar-provider-settings"] });
      toast({
        title: "Microsoft Calendar User Saved",
        description: "Your Microsoft Outlook calendar user has been configured.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to Save Microsoft Calendar User",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const saveGoogleCalendarUserMutation = useMutation({
    mutationFn: async (userEmail: string) => {
      const res = await apiRequest("PUT", "/api/flow-automation/google-calendar-user", { userEmail });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/flow-automation/calendar-provider-settings"] });
      toast({
        title: "Google Calendar User Saved",
        description: "Your Google Calendar user has been configured.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to Save Google Calendar User",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const testMicrosoftConnectionMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/flow-automation/microsoft-calendar/test", {});
      return res.json();
    },
    onSuccess: (data) => {
      setMicrosoftTestResult(data);
      if (data.success) {
        toast({
          title: "Microsoft Connection Successful",
          description: data.message,
        });
      } else {
        toast({
          title: "Microsoft Connection Failed",
          description: data.error,
          variant: "destructive",
        });
      }
    },
    onError: (error: any) => {
      setMicrosoftTestResult({ success: false, message: error.message });
      toast({
        title: "Microsoft Connection Test Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const testGoogleConnectionMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/flow-automation/google-calendar/test", {});
      return res.json();
    },
    onSuccess: (data) => {
      setGoogleTestResult(data);
      if (data.success) {
        toast({
          title: "Google Calendar Connection Successful",
          description: data.message,
        });
      } else {
        toast({
          title: "Google Calendar Connection Failed",
          description: data.error,
          variant: "destructive",
        });
      }
    },
    onError: (error: any) => {
      setGoogleTestResult({ success: false, message: error.message });
      toast({
        title: "Google Connection Test Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Connect Google Calendar - opens OAuth flow
  const connectGoogleCalendarMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("GET", "/api/flow-automation/google-calendar/authorize");
      return res.json();
    },
    onSuccess: (data) => {
      if (data.authUrl) {
        // Redirect to Google OAuth
        window.location.href = data.authUrl;
      } else {
        toast({
          title: "Connection Failed",
          description: data.error || "Failed to get authorization URL",
          variant: "destructive",
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: "Connection Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Disconnect Google Calendar
  const disconnectGoogleCalendarMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("DELETE", "/api/flow-automation/google-calendar/disconnect", {});
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/flow-automation/google-calendar/status"] });
      queryClient.invalidateQueries({ queryKey: ["/api/flow-automation/calendar-provider-settings"] });
      toast({
        title: "Disconnected",
        description: "Google Calendar has been disconnected successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Disconnect Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Connect Microsoft Calendar - opens OAuth flow
  const connectMicrosoftCalendarMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("GET", "/api/flow-automation/microsoft-calendar/authorize");
      return res.json();
    },
    onSuccess: (data) => {
      if (data.authUrl) {
        // Redirect to Microsoft OAuth
        window.location.href = data.authUrl;
      } else {
        toast({
          title: "Connection Failed",
          description: data.error || "Failed to get authorization URL",
          variant: "destructive",
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: "Connection Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Disconnect Microsoft Calendar
  const disconnectMicrosoftCalendarMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("DELETE", "/api/flow-automation/microsoft-calendar/disconnect", {});
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/flow-automation/microsoft-calendar/status"] });
      queryClient.invalidateQueries({ queryKey: ["/api/flow-automation/calendar-provider-settings"] });
      toast({
        title: "Disconnected",
        description: "Microsoft Calendar has been disconnected successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Disconnect Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Check if email domain is pre-approved
  const isEmailDomainApproved = (email: string): ApprovedDomain | undefined => {
    if (!email.includes('@')) return undefined;
    const domain = email.split('@')[1].toLowerCase();
    return approvedDomains.find(d => d.domain === domain && d.isActive);
  };

  // Invite team member
  const inviteTeamMemberMutation = useMutation({
    mutationFn: async ({ email, name, usesAdminCalendar }: { email: string; name?: string; usesAdminCalendar?: boolean }) => {
      const res = await apiRequest("POST", "/api/flow-automation/calendar-team-members/invite", { email, name, usesAdminCalendar });
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/flow-automation/calendar-team-members"] });
      setInviteEmail("");
      setInviteName("");
      setUsesAdminCalendar(false);

      if (data.usesAdminCalendar) {
        // Team member added using admin's calendar (no mailbox required)
        toast({
          title: "Host Added",
          description: `${data.message}. Meetings will be scheduled on your calendar (${data.adminCalendarEmail}).`,
        });
      } else if (data.domainApproved) {
        // Domain was approved - team member auto-connected
        toast({
          title: "Team Member Added",
          description: `${data.message}. They will use your organization's calendar (${data.adminCalendarEmail}).`,
        });
      } else {
        // Domain not approved - invite sent for OAuth
        toast({
          title: "Invitation Sent",
          description: `Invite sent. They need to connect their own calendar to join the team.`,
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: "Failed to Send Invitation",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Remove team member
  const removeTeamMemberMutation = useMutation({
    mutationFn: async (memberId: string) => {
      const res = await apiRequest("DELETE", `/api/flow-automation/calendar-team-members/${memberId}`, {});
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/flow-automation/calendar-team-members"] });
      toast({
        title: "Team Member Removed",
        description: "The team member has been removed successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to Remove Team Member",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Resend invitation
  const resendInviteMutation = useMutation({
    mutationFn: async (memberId: string) => {
      const res = await apiRequest("POST", `/api/flow-automation/calendar-team-members/${memberId}/resend-invite`, {});
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/flow-automation/calendar-team-members"] });
      toast({
        title: "Invitation Resent",
        description: "A new invitation has been sent to the team member.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to Resend Invitation",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Add manual host email (for non-admin users)
  const addManualHostMutation = useMutation({
    mutationFn: async ({ email, name }: { email: string; name?: string }) => {
      const res = await apiRequest("POST", "/api/flow-automation/calendar-team-members/add-manual-host", { email, name });
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/flow-automation/calendar-team-members"] });
      queryClient.invalidateQueries({ queryKey: ["/api/flow-automation/calendar-team-members/my-host"] });
      queryClient.invalidateQueries({ queryKey: ["/api/flow-automation/google-calendar/status"] });
      queryClient.invalidateQueries({ queryKey: ["/api/flow-automation/microsoft-calendar/status"] });
      setManualHostEmail("");
      setManualHostName("");
      toast({
        title: "Host Email Added",
        description: data.message || "Host email has been added successfully. Meetings will be scheduled for this email.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to Add Host",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Copy invite link helper
  const copyInviteLink = async (member: TeamMember) => {
    if (!member.inviteToken) return;
    const inviteUrl = `${window.location.origin}/calendar-connect?token=${member.inviteToken}`;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopiedInviteLink(member.id);
      setTimeout(() => setCopiedInviteLink(null), 2000);
      toast({
        title: "Link Copied",
        description: "Invite link copied to clipboard.",
      });
    } catch {
      toast({
        title: "Failed to Copy",
        description: "Could not copy link to clipboard.",
        variant: "destructive",
      });
    }
  };

  const getAppointmentsForDate = (date: Date) => {
    return appointments.filter((apt) => isSameDay(new Date(apt.scheduledFor), date));
  };

  const getMonthDays = () => {
    const start = startOfWeek(startOfMonth(currentDate));
    const end = endOfWeek(endOfMonth(currentDate));
    return eachDayOfInterval({ start, end });
  };

  const handlePrevious = () => {
    setCurrentDate(subMonths(currentDate, 1));
  };

  const handleNext = () => {
    setCurrentDate(addMonths(currentDate, 1));
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const toggleWorkingDay = (day: string) => {
    const workingDays = settingsData.workingDays || [];
    if (workingDays.includes(day)) {
      setSettingsData({ ...settingsData, workingDays: workingDays.filter((d) => d !== day) });
    } else {
      setSettingsData({ ...settingsData, workingDays: [...workingDays, day] });
    }
  };

  const calendarDays = [
    { key: "sun", label: t("appointments.calendar.days.sun") },
    { key: "mon", label: t("appointments.calendar.days.mon") },
    { key: "tue", label: t("appointments.calendar.days.tue") },
    { key: "wed", label: t("appointments.calendar.days.wed") },
    { key: "thu", label: t("appointments.calendar.days.thu") },
    { key: "fri", label: t("appointments.calendar.days.fri") },
    { key: "sat", label: t("appointments.calendar.days.sat") },
  ];

  const workingDayOptions = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">{t("appointments.loading")}</div>
        </div>
      </div>
    );
  }

  const totalAppointments = appointments.length;
  const upcomingAppointments = appointments.filter(apt => new Date(apt.scheduledFor) > new Date()).length;
  const completedAppointments = appointments.filter(apt => apt.status === 'completed').length;
  const cancelledAppointments = appointments.filter(apt => apt.status === 'cancelled').length;

  return (
    <div className="space-y-6">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-rose-50 via-pink-100/50 to-red-50 dark:from-rose-950/40 dark:via-pink-900/30 dark:to-red-950/40 border border-rose-100 dark:border-rose-900/50 p-6 md:p-8">
        <div className="absolute inset-0 bg-grid-slate-200/50 dark:bg-grid-slate-700/20 [mask-image:linear-gradient(0deg,transparent,rgba(255,255,255,0.5))]" />
        <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center shadow-lg shadow-rose-500/25">
              <CalendarDays className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground" data-testid="text-page-title">
                {t("appointments.title")}
              </h1>
              <p className="text-muted-foreground mt-0.5">{t("appointments.subtitle")}</p>
            </div>
          </div>
{isCalendarAdmin && (
          <Button
            onClick={() => setSettingsOpen(true)}
            variant="outline"
            className="border-rose-200 dark:border-rose-800"
            data-testid="button-settings"
          >
            <Settings className="h-4 w-4 mr-2" />
            {t("common.settings")}
          </Button>
          )}
        </div>

        <div className="relative mt-6 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <div className="bg-white/80 dark:bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-rose-100/50 dark:border-rose-800/30">
            <div className="flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-rose-600 dark:text-rose-400" />
              <div className="text-2xl font-bold text-rose-700 dark:text-rose-300">{totalAppointments}</div>
            </div>
            <div className="text-rose-600/70 dark:text-rose-400/70 text-sm">{t("appointments.stats.total")}</div>
          </div>
          <div className="bg-white/80 dark:bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-pink-100/50 dark:border-pink-800/30">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-pink-600 dark:text-pink-400" />
              <div className="text-2xl font-bold text-pink-700 dark:text-pink-300">{upcomingAppointments}</div>
            </div>
            <div className="text-pink-600/70 dark:text-pink-400/70 text-sm">{t("appointments.stats.upcoming")}</div>
          </div>
          <div className="bg-white/80 dark:bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-emerald-100/50 dark:border-emerald-800/30">
            <div className="flex items-center gap-2">
              <CalendarCheck className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
              <div className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">{completedAppointments}</div>
            </div>
            <div className="text-emerald-600/70 dark:text-emerald-400/70 text-sm">{t("appointments.stats.completed")}</div>
          </div>
          <div className="bg-white/80 dark:bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-slate-100/50 dark:border-slate-700/30">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-slate-600 dark:text-slate-400" />
              <div className="text-2xl font-bold text-slate-700 dark:text-slate-300">{cancelledAppointments}</div>
            </div>
            <div className="text-slate-600/70 dark:text-slate-400/70 text-sm">{t("appointments.stats.cancelled")}</div>
          </div>
        </div>
      </div>

      {/* Team Member View - Show when user has no admin calendar connected */}
      {!isCalendarAdmin && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-primary" />
              <CardTitle>Setup Your Calendar</CardTitle>
            </div>
            <CardDescription>
              Configure how AI agents will schedule meetings on your behalf.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Cal.com account actually being used for availability/booking - distinct
                from the host email below, which only controls who gets invited. */}
            {calcomConnectedAccount?.connected ? (
              <div className="p-4 rounded-lg border bg-violet-50 dark:bg-violet-950/30 border-violet-200 dark:border-violet-800">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-violet-100 dark:bg-violet-900/50 flex items-center justify-center">
                    <Link2 className="h-5 w-5 text-violet-600 dark:text-violet-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-violet-800 dark:text-violet-200">
                      Cal.com Account Connected
                    </p>
                    <p className="text-sm text-violet-700 dark:text-violet-300">
                      {calcomConnectedAccount.email}
                    </p>
                  </div>
                  <Badge variant="outline" className="text-violet-600 border-violet-600">
                    Active
                  </Badge>
                </div>
                <p className="mt-3 text-xs text-violet-700 dark:text-violet-300">
                  Real-time availability is checked and meetings are created on this Cal.com account.
                </p>
              </div>
            ) : (
              <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-700 dark:text-amber-300">
                    No Cal.com account is connected yet. Add a Cal.com API key in your Integrations settings to enable real-time availability checking.
                  </p>
                </div>
              </div>
            )}

            {/* Show domain approval status if approved */}
            {myDomainApproval?.approved && (
              <div className="p-4 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-green-900 dark:text-green-100">
                      Your Domain is Approved
                    </p>
                    <p className="text-xs text-green-700 dark:text-green-300">
                      Domain <strong>{myDomainApproval.domain}</strong> is approved by {myDomainApproval.adminEmail}.
                      You can connect your calendar to start scheduling meetings.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Step 1: Admin Choice - Show when not yet selected */}
            {isOrgAdmin === null && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-base font-semibold">Are you an Admin for your Organisation?</Label>
                  <p className="text-sm text-muted-foreground">
                    This helps us determine how to set up your calendar integration.
                  </p>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  {/* Yes - Admin Option */}
                  <Button
                    variant="outline"
                    className="h-auto py-6 px-4 flex flex-col items-center gap-3 hover:border-primary hover:bg-primary/5 border-2"
                    onClick={() => setIsOrgAdmin(true)}
                  >
                    <div className="h-12 w-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                      <Shield className="h-6 w-6 text-green-600 dark:text-green-400" />
                    </div>
                    <div className="text-center">
                      <span className="font-semibold text-lg block">Yes, I'm an Admin</span>
                      <span className="text-xs text-muted-foreground mt-1 block">
                        Connect your Google or Microsoft account to enable full calendar features
                      </span>
                    </div>
                  </Button>

                  {/* No - Not Admin Option */}
                  <Button
                    variant="outline"
                    className="h-auto py-6 px-4 flex flex-col items-center gap-3 hover:border-primary hover:bg-primary/5 border-2"
                    onClick={() => setIsOrgAdmin(false)}
                  >
                    <div className="h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                      <Mail className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="text-center">
                      <span className="font-semibold text-lg block">No, I'm not an Admin</span>
                      <span className="text-xs text-muted-foreground mt-1 block">
                        Simply add any email address as a host for meetings
                      </span>
                    </div>
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2a: Admin Flow - Connect Google/Microsoft */}
            {isOrgAdmin === true && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Connect Your Calendar Provider</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsOrgAdmin(null)}
                    className="text-xs text-muted-foreground"
                  >
                    Change selection
                  </Button>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  {/* Google Calendar Button */}
                  <Button
                    variant="outline"
                    className="h-auto py-4 px-4 flex flex-col items-center gap-2 hover:border-primary hover:bg-primary/5"
                    onClick={() => connectGoogleCalendarMutation.mutate()}
                    disabled={connectGoogleCalendarMutation.isPending}
                  >
                    {connectGoogleCalendarMutation.isPending ? (
                      <Loader2 className="h-8 w-8 animate-spin" />
                    ) : (
                      <svg className="h-8 w-8" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                    )}
                    <span className="font-medium">Google Calendar</span>
                    <span className="text-xs text-muted-foreground">Connect with Google</span>
                  </Button>

                  {/* Microsoft Calendar Button */}
                  <Button
                    variant="outline"
                    className="h-auto py-4 px-4 flex flex-col items-center gap-2 hover:border-primary hover:bg-primary/5"
                    onClick={() => connectMicrosoftCalendarMutation.mutate()}
                    disabled={connectMicrosoftCalendarMutation.isPending}
                  >
                    {connectMicrosoftCalendarMutation.isPending ? (
                      <Loader2 className="h-8 w-8 animate-spin" />
                    ) : (
                      <svg className="h-8 w-8" viewBox="0 0 24 24">
                        <path fill="#F25022" d="M11.4 24H0V12.6h11.4V24z"/>
                        <path fill="#00A4EF" d="M24 24H12.6V12.6H24V24z"/>
                        <path fill="#7FBA00" d="M11.4 11.4H0V0h11.4v11.4z"/>
                        <path fill="#FFB900" d="M24 11.4H12.6V0H24v11.4z"/>
                      </svg>
                    )}
                    <span className="font-medium">Microsoft Outlook</span>
                    <span className="text-xs text-muted-foreground">Connect with Microsoft</span>
                  </Button>
                </div>
                <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    As an admin, connecting your calendar will enable real-time availability checking, automatic meeting creation, and team member management.
                  </p>
                </div>
              </div>
            )}

            {/* Step 2b: Non-Admin Flow - Add/Edit Manual Email */}
            {isOrgAdmin === false && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">
                    {hasManualHostEmail ? "Host Email Configured" : "Add Host Email"}
                  </Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsOrgAdmin(null)}
                    className="text-xs text-muted-foreground"
                  >
                    Change selection
                  </Button>
                </div>

                {/* Show existing host email if configured */}
                {hasManualHostEmail && (
                  <div className="p-4 rounded-lg border bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-green-100 dark:bg-green-900/50 flex items-center justify-center">
                        <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-green-800 dark:text-green-200">
                          {manualHostData?.hostEmail}
                        </p>
                        {manualHostData?.hostName && (
                          <p className="text-sm text-green-600 dark:text-green-400">
                            {manualHostData.hostName}
                          </p>
                        )}
                      </div>
                      <Badge variant="outline" className="text-green-600 border-green-600">
                        Active
                      </Badge>
                    </div>
                    <p className="mt-3 text-xs text-green-700 dark:text-green-300">
                      Meeting invitations will be sent to this email address.
                    </p>
                  </div>
                )}

                {/* Form to add/update host email */}
                <div className="p-4 rounded-lg border bg-muted/20">
                  <div className="space-y-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="manualHostEmail" className="text-sm">
                        {hasManualHostEmail ? "Update Host Email Address" : "Host Email Address"}
                      </Label>
                      <Input
                        id="manualHostEmail"
                        type="email"
                        placeholder="host@example.com"
                        value={manualHostEmail}
                        onChange={(e) => setManualHostEmail(e.target.value)}
                      />
                      <p className="text-xs text-muted-foreground">
                        {hasManualHostEmail
                          ? "Enter a new email to update the host email."
                          : "Enter any email address that will receive meeting invitations."}
                      </p>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="manualHostName" className="text-sm">Host Name (Optional)</Label>
                      <Input
                        id="manualHostName"
                        type="text"
                        placeholder="John Doe"
                        value={manualHostName}
                        onChange={(e) => setManualHostName(e.target.value)}
                      />
                    </div>
                    <Button
                      onClick={() => addManualHostMutation.mutate({
                        email: manualHostEmail,
                        name: manualHostName || undefined
                      })}
                      disabled={!manualHostEmail || addManualHostMutation.isPending}
                      className="w-full"
                    >
                      {addManualHostMutation.isPending ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Mail className="h-4 w-4 mr-2" />
                      )}
                      {hasManualHostEmail ? "Update Host Email" : "Add Host Email"}
                    </Button>
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-amber-700 dark:text-amber-300">
                      Without calendar integration, AI agents won't be able to check real-time availability. Meeting invites will be sent to this email, and the host will need to manage conflicts manually.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Calendar & appointments view - Show whenever bookings can happen for this user */}
      {hasBookingSetup && (
      <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={handlePrevious} data-testid="button-prev-month">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={handleToday} data-testid="button-today">
                {t("appointments.calendar.today")}
              </Button>
              <Button variant="outline" size="sm" onClick={handleNext} data-testid="button-next-month">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <h2 className="text-xl font-semibold" data-testid="text-current-month">
              {format(currentDate, "MMMM yyyy")}
            </h2>
            <Tabs value={view} onValueChange={(v: any) => setView(v)}>
              <TabsList>
                <TabsTrigger value="day" data-testid="tab-day">{t("appointments.calendar.day")}</TabsTrigger>
                <TabsTrigger value="week" data-testid="tab-week">{t("appointments.calendar.week")}</TabsTrigger>
                <TabsTrigger value="month" data-testid="tab-month">{t("appointments.calendar.month")}</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent>
          {view === "month" && (
            <div>
              <div className="grid grid-cols-7 gap-2 mb-2">
                {calendarDays.map((day) => (
                  <div key={day.key} className="text-center text-sm font-semibold text-muted-foreground py-2">
                    {day.label}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-2">
                {getMonthDays().map((day, index) => {
                  const dayAppointments = getAppointmentsForDate(day);
                  const isCurrentMonth = isSameMonth(day, currentDate);
                  const isToday = isSameDay(day, new Date());

                  return (
                    <div
                      key={index}
                      className={`min-h-24 p-2 border rounded-md cursor-pointer ${
                        !isCurrentMonth ? "bg-muted/30 text-muted-foreground" : ""
                      } ${isToday ? "border-primary bg-primary/5" : ""} ${
                        selectedDate && isSameDay(day, selectedDate) ? "ring-2 ring-primary border-primary" : ""
                      } hover-elevate`}
                      onClick={() => setSelectedDate(day)}
                      data-testid={`calendar-day-${format(day, "yyyy-MM-dd")}`}
                    >
                      <div className="text-sm font-medium mb-1">{format(day, "d")}</div>
                      {dayAppointments.length > 0 && (
                        <div className="space-y-1">
                          {dayAppointments.slice(0, 2).map((apt) => (
                            <div
                              key={apt.id}
                              className="text-xs p-1 bg-primary/10 border border-primary/20 rounded truncate"
                              title={`${apt.contactName} - ${formatDate(apt.scheduledFor, "h:mm a")}`}
                              data-testid={`appointment-${apt.id}`}
                            >
                              <Clock className="h-3 w-3 inline mr-1" />
                              {formatDate(apt.scheduledFor, "h:mm a")}
                            </div>
                          ))}
                          {dayAppointments.length > 2 && (
                            <div className="text-xs text-muted-foreground">
                              {t("appointments.calendar.more", { count: dayAppointments.length - 2 })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {view === "week" && (
            <div className="text-center py-12 text-muted-foreground">
              {t("appointments.calendar.weekViewSoon")}
            </div>
          )}

          {view === "day" && (
            <div className="text-center py-12 text-muted-foreground">
              {t("appointments.calendar.dayViewSoon")}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("appointments.upcomingAppointments")}</CardTitle>
          <CardDescription>{t("appointments.upcomingDescription")}</CardDescription>
        </CardHeader>
        <CardContent>
          {(() => {
            // Filter for upcoming appointments (scheduled >= now) and sort by soonest first
            const now = new Date();
            const upcomingAppointments = appointments
              .filter((apt) => new Date(apt.scheduledFor) >= now)
              .sort((a, b) => new Date(a.scheduledFor).getTime() - new Date(b.scheduledFor).getTime());
            
            if (upcomingAppointments.length === 0) {
              return (
                <div className="flex flex-col items-center justify-center py-12">
                  <CalendarIcon className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">{t("appointments.noAppointments")}</h3>
                  <p className="text-sm text-muted-foreground text-center max-w-md">
                    {t("appointments.noAppointmentsDescription")}
                  </p>
                </div>
              );
            }
            
            return (
              <ScrollArea className="h-96">
                <div className="space-y-3 pr-4">
                  {upcomingAppointments.map((apt) => (
                    <Card key={apt.id} className="hover-elevate">
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <User className="h-4 w-4 text-muted-foreground" />
                              <div className="font-semibold" data-testid={`text-contact-name-${apt.id}`}>
                                {apt.contactName}
                              </div>
                              <Badge variant={apt.status === "confirmed" ? "default" : "secondary"}>
                                {apt.status}
                              </Badge>
                              {apt.metadata?.phoneDiscrepancy && (
                                <Badge variant="outline" className="text-amber-600 border-amber-300 dark:text-amber-400 dark:border-amber-700">
                                  <AlertCircle className="h-3 w-3 mr-1" />
                                  Phone mismatch
                                </Badge>
                              )}
                            </div>
                            <div className="text-sm text-muted-foreground space-y-0.5">
                              <div className="flex items-center gap-1">
                                <span className="font-medium text-foreground">{apt.contactPhone}</span>
                                {apt.contactEmail && <span>• {apt.contactEmail}</span>}
                              </div>
                              {apt.metadata?.phoneDiscrepancy && apt.metadata.aiCollectedPhone && (
                                <div className="text-xs text-amber-600 dark:text-amber-400">
                                  AI heard: {apt.metadata.aiCollectedPhone}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-medium" data-testid={`text-scheduled-time-${apt.id}`}>
                              {formatDate(apt.scheduledFor, "MMM d, yyyy")}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {formatDate(apt.scheduledFor, "h:mm a")}
                            </div>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          {apt.serviceName && (
                            <div>
                              <span className="text-muted-foreground">{t("appointments.details.service")}: </span>
                              <span className="font-medium">{apt.serviceName}</span>
                            </div>
                          )}
                          <div>
                            <span className="text-muted-foreground">{t("appointments.duration")}: </span>
                            <span className="font-medium">{t("appointments.durationMinutes", { count: apt.duration })}</span>
                          </div>
                          {(apt.metadata?.hostName || apt.metadata?.hostEmail) && (
                            <div>
                              <span className="text-muted-foreground">Host: </span>
                              <span className="font-medium">
                                {apt.metadata?.hostName || apt.metadata?.hostEmail}
                              </span>
                            </div>
                          )}
                          {apt.metadata?.calcomBookingUid && (
                            <div>
                              <span className="text-muted-foreground">Booking ref: </span>
                              <span className="font-medium font-mono text-xs">{apt.metadata.calcomBookingUid}</span>
                            </div>
                          )}
                        </div>
                        {(apt.metadata?.meetingUrl || apt.googleMeetLink || apt.teamsLink) && (
                          <a
                            href={apt.metadata?.meetingUrl || apt.googleMeetLink || apt.teamsLink || undefined}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-3 inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                            data-testid={`link-meeting-${apt.id}`}
                          >
                            <Link2 className="h-3.5 w-3.5" />
                            Join meeting
                          </a>
                        )}
                        {apt.notes && (
                          <div className="mt-3 p-2 bg-muted/50 rounded-md text-sm">
                            <span className="text-muted-foreground">{t("appointments.details.notes")}: </span>
                            {apt.notes}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </ScrollArea>
            );
          })()}
        </CardContent>
      </Card>
      </>
      )}

      <Dialog open={settingsOpen} onOpenChange={(open) => { setSettingsOpen(open); if (!open) { setSettingsTab("general"); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid="text-settings-title">{t("appointments.settings.title")}</DialogTitle>
            <DialogDescription>{t("appointments.settings.description")}</DialogDescription>
          </DialogHeader>

          <Tabs value={settingsTab} onValueChange={(v: any) => setSettingsTab(v)} className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="general">General</TabsTrigger>
              <TabsTrigger value="calendar" className="flex items-center gap-2">
                Calendar
                {(calendarProviderSettings?.microsoftConfigured || calendarProviderSettings?.googleConfigured) && (
                  <CheckCircle2 className="h-3 w-3 text-green-500" />
                )}
              </TabsTrigger>
              <TabsTrigger value="team" className="flex items-center gap-2">
                <Users className="h-3 w-3" />
                Team
                {teamMembers.length > 0 && (
                  <Badge variant="secondary" className="h-4 px-1 text-[10px]">
                    {teamMembers.length}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="general" className="space-y-6 py-4">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>{t("appointments.settings.allowOverlap")}</Label>
                    <p className="text-sm text-muted-foreground">
                      {t("appointments.settings.allowOverlapDescription")}
                    </p>
                  </div>
                  <Switch
                    checked={settingsData.allowOverlap}
                    onCheckedChange={(checked) => setSettingsData({ ...settingsData, allowOverlap: checked })}
                    data-testid="switch-allow-overlap"
                  />
                </div>

                <Separator />

                <div className="space-y-2">
                  <Label htmlFor="buffer-time">{t("appointments.settings.bufferTime")}</Label>
                  <Input
                    id="buffer-time"
                    type="number"
                    value={settingsData.bufferTime}
                    onChange={(e) => setSettingsData({ ...settingsData, bufferTime: parseInt(e.target.value) || 0 })}
                    data-testid="input-buffer-time"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="max-per-day">{t("appointments.settings.maxPerDay")}</Label>
                  <Input
                    id="max-per-day"
                    type="number"
                    placeholder={t("appointments.settings.maxPerDayPlaceholder")}
                    value={settingsData.maxPerDay || ""}
                    onChange={(e) =>
                      setSettingsData({ ...settingsData, maxPerDay: e.target.value ? parseInt(e.target.value) : null })
                    }
                    data-testid="input-max-per-day"
                  />
                </div>

                <Separator />

                <div className="space-y-3">
                  <Label>{t("appointments.settings.workingHours")}</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="hours-start">{t("appointments.settings.startTime")}</Label>
                      <Input
                        id="hours-start"
                        type="time"
                        value={settingsData.workingHoursStart}
                        onChange={(e) => setSettingsData({ ...settingsData, workingHoursStart: e.target.value })}
                        data-testid="input-hours-start"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="hours-end">{t("appointments.settings.endTime")}</Label>
                      <Input
                        id="hours-end"
                        type="time"
                        value={settingsData.workingHoursEnd}
                        onChange={(e) => setSettingsData({ ...settingsData, workingHoursEnd: e.target.value })}
                        data-testid="input-hours-end"
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                <div className="space-y-3">
                  <Label>{t("appointments.settings.workingDays")}</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {workingDayOptions.map((day) => (
                      <div
                        key={day}
                        className={`p-3 border rounded-md cursor-pointer hover-elevate ${
                          settingsData.workingDays?.includes(day) ? "border-primary bg-primary/5" : ""
                        }`}
                        onClick={() => toggleWorkingDay(day)}
                        data-testid={`day-option-${day}`}
                      >
                        <div className="flex items-center gap-2">
                          <div
                            className={`h-4 w-4 rounded border ${
                              settingsData.workingDays?.includes(day)
                                ? "bg-primary border-primary"
                                : "border-muted-foreground"
                            }`}
                          />
                          <span className="text-sm font-medium capitalize">{t(`appointments.settings.days.${day}`)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setSettingsOpen(false)} data-testid="button-cancel-settings">
                  {t("common.cancel")}
                </Button>
                <Button onClick={() => saveSettingsMutation.mutate()} disabled={saveSettingsMutation.isPending} data-testid="button-save-settings">
                  {t("appointments.settings.saveSettings")}
                </Button>
              </DialogFooter>
            </TabsContent>

            <TabsContent value="calendar" className="space-y-6 py-4">
              {/* Calendar Provider Selection */}
              <div className="space-y-3">
                <Label>Calendar Provider</Label>
                <p className="text-sm text-muted-foreground">
                  Choose which calendar service to use for availability checking and meeting invites.
                </p>
                <Select
                  value={calendarProvider}
                  onValueChange={(value: CalendarProvider) => {
                    setCalendarProvider(value);
                    saveCalendarProviderMutation.mutate(value);
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a calendar provider" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="microsoft">
                      <div className="flex items-center gap-2">
                        <CalendarDays className="h-4 w-4" />
                        <span>Microsoft Outlook</span>
                        {calendarProviderSettings?.microsoftConfigured && <CheckCircle2 className="h-3 w-3 text-green-500 ml-1" />}
                      </div>
                    </SelectItem>
                    <SelectItem value="google">
                      <div className="flex items-center gap-2">
                        <CalendarDays className="h-4 w-4" />
                        <span>Google Calendar</span>
                        {calendarProviderSettings?.googleConfigured && <CheckCircle2 className="h-3 w-3 text-green-500 ml-1" />}
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              {/* Microsoft Outlook Settings */}
              {calendarProvider === 'microsoft' && (
                <>
                  <div className="rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-800 p-4">
                    <div className="flex items-start gap-3">
                      <CalendarDays className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                          Microsoft Outlook Integration
                        </p>
                        <p className="text-sm text-blue-700 dark:text-blue-300">
                          Connect your Microsoft 365 account to check calendar availability and schedule meetings automatically during voice calls.
                        </p>
                        <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                          Configuration can be managed in <strong>Admin Dashboard → Global Settings</strong> under "Microsoft Azure AD / Calendar".
                        </p>
                      </div>
                    </div>
                  </div>

                  {microsoftCalendarStatus?.connected ? (
                    <>
                      {/* Connected State */}
                      <div className="p-4 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
                            <div className="space-y-1">
                              <p className="text-sm font-medium text-green-900 dark:text-green-100">
                                Microsoft Calendar Connected
                              </p>
                              {microsoftCalendarStatus.email && (
                                <p className="text-xs text-green-700 dark:text-green-300">
                                  <strong>Account:</strong> {microsoftCalendarStatus.email}
                                </p>
                              )}
                              {microsoftCalendarStatus.lastUsedAt && (
                                <p className="text-xs text-green-600 dark:text-green-400">
                                  Last used: {formatDate(microsoftCalendarStatus.lastUsedAt, 'M/d/yyyy, h:mm:ss a')}
                                </p>
                              )}
                            </div>
                          </div>
                          {disconnectMicrosoftCalendarMutation.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => disconnectMicrosoftCalendarMutation.mutate()}
                              className="text-destructive hover:text-destructive"
                            >
                              Disconnect
                            </Button>
                          )}
                        </div>
                      </div>

                      {microsoftCalendarStatus.lastError && (
                        <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                          <div className="flex items-start gap-2">
                            <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5" />
                            <p className="text-xs text-amber-700 dark:text-amber-300">
                              Last error: {microsoftCalendarStatus.lastError}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Optional: Per-tenant Microsoft Calendar Email Override */}
                      <div className="space-y-2 pt-2 border-t">
                        <Label htmlFor="microsoft-calendar-user" className="text-sm font-medium">
                          Calendar Email (Optional Override)
                        </Label>
                        <div className="flex gap-2">
                          <Input
                            id="microsoft-calendar-user"
                            type="email"
                            placeholder="e.g., calendar@company.com"
                            value={microsoftCalendarUserId}
                            onChange={(e) => setMicrosoftCalendarUserId(e.target.value)}
                            className="flex-1"
                          />
                          <Button
                            onClick={() => saveMicrosoftCalendarUserMutation.mutate(microsoftCalendarUserId)}
                            disabled={saveMicrosoftCalendarUserMutation.isPending || !microsoftCalendarUserId.trim()}
                          >
                            {saveMicrosoftCalendarUserMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            Save
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Optionally specify a different Microsoft Calendar email to check for availability.
                          Leave blank to use the connected account's primary calendar.
                        </p>
                        {microsoftCalendarUserId && (
                          <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
                            <p className="text-xs text-blue-700 dark:text-blue-300">
                              <strong>Calendar Override:</strong> <code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">{microsoftCalendarUserId}</code>
                            </p>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Not Connected State */}
                      <div className="p-4 rounded-lg border border-dashed border-muted-foreground/25 bg-muted/20">
                        <div className="flex flex-col items-center justify-center py-6 space-y-4">
                          <div className="p-3 rounded-full bg-muted">
                            <CalendarDays className="h-8 w-8 text-muted-foreground" />
                          </div>
                          <div className="text-center space-y-1">
                            <p className="text-sm font-medium">Connect Your Microsoft Calendar</p>
                            <p className="text-xs text-muted-foreground">
                              Sign in with your Microsoft account to enable calendar availability checking and automatic meeting scheduling.
                            </p>
                          </div>
                          <Button
                            onClick={() => connectMicrosoftCalendarMutation.mutate()}
                            disabled={connectMicrosoftCalendarMutation.isPending || !calendarProviderSettings?.microsoftConfigured}
                            className="mt-2"
                          >
                            {connectMicrosoftCalendarMutation.isPending ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
                                <path fill="currentColor" d="M11.4 24H0V12.6h11.4V24zM24 24H12.6V12.6H24V24zM11.4 11.4H0V0h11.4v11.4zm12.6 0H12.6V0H24v11.4z"/>
                              </svg>
                            )}
                            Connect with Microsoft
                          </Button>
                          {!calendarProviderSettings?.microsoftConfigured && (
                            <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                              Azure AD app not configured. Contact your administrator to set up in Global Settings.
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="rounded-lg border p-4 space-y-2">
                        <h4 className="text-sm font-medium">What you get:</h4>
                        <ul className="text-xs text-muted-foreground space-y-1">
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="h-3 w-3 text-green-500" />
                            Real-time calendar availability checking
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="h-3 w-3 text-green-500" />
                            Calendar events created on your behalf
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="h-3 w-3 text-green-500" />
                            Meeting invites sent to attendees
                          </li>
                        </ul>
                      </div>
                    </>
                  )}

                  {microsoftTestResult && (
                    <div className={`p-4 rounded-lg border ${microsoftTestResult.success ? 'bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800'}`}>
                      <div className="flex items-start gap-3">
                        {microsoftTestResult.success ? (
                          <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
                        ) : (
                          <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5" />
                        )}
                        <div className="space-y-2">
                          <p className={`text-sm font-medium ${microsoftTestResult.success ? 'text-green-900 dark:text-green-100' : 'text-red-900 dark:text-red-100'}`}>
                            {microsoftTestResult.message}
                          </p>
                          {microsoftTestResult.success && microsoftTestResult.slots && microsoftTestResult.slots.length > 0 && (
                            <div className="mt-2">
                              <p className="text-xs text-green-700 dark:text-green-300 font-medium mb-1">Available Slots:</p>
                              <ul className="text-xs text-green-600 dark:text-green-400 space-y-0.5">
                                {microsoftTestResult.slots.map((slot, index) => (
                                  <li key={index} className="flex items-center gap-1">
                                    <span className="w-1 h-1 bg-green-500 rounded-full"></span>
                                    {slot}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  <DialogFooter className="gap-2 sm:gap-0">
                    <Button
                      variant="outline"
                      onClick={() => testMicrosoftConnectionMutation.mutate()}
                      disabled={testMicrosoftConnectionMutation.isPending}
                    >
                      {testMicrosoftConnectionMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      Test Microsoft Connection
                    </Button>
                  </DialogFooter>
                </>
              )}

              {/* Google Calendar Settings */}
              {calendarProvider === 'google' && (
                <>
                  <div className="rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-800 p-4">
                    <div className="flex items-start gap-3">
                      <CalendarDays className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                          Google Calendar Integration
                        </p>
                        <p className="text-sm text-blue-700 dark:text-blue-300">
                          Connect your Google account to check calendar availability and create Google Meet links automatically during voice calls.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Connected State */}
                  {googleCalendarStatus?.connected ? (
                    <>
                      <div className="p-4 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
                            <div className="space-y-1">
                              <p className="text-sm font-medium text-green-900 dark:text-green-100">
                                Google Calendar Connected
                              </p>
                              {googleCalendarStatus.email && (
                                <p className="text-sm text-green-700 dark:text-green-300">
                                  {googleCalendarStatus.email}
                                </p>
                              )}
                              {googleCalendarStatus.isShared && (
                                <p className="text-xs text-green-600 dark:text-green-400">
                                  Using shared calendar configuration
                                </p>
                              )}
                            </div>
                          </div>
                          {!googleCalendarStatus.isShared && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => disconnectGoogleCalendarMutation.mutate()}
                              disabled={disconnectGoogleCalendarMutation.isPending}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              {disconnectGoogleCalendarMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                              Disconnect
                            </Button>
                          )}
                        </div>
                      </div>

                      {googleCalendarStatus.lastError && (
                        <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                          <div className="flex items-start gap-2">
                            <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5" />
                            <p className="text-xs text-amber-700 dark:text-amber-300">
                              Last error: {googleCalendarStatus.lastError}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Optional: Per-tenant Google Calendar Email Override */}
                      <div className="space-y-2 pt-2 border-t">
                        <Label htmlFor="google-calendar-user" className="text-sm font-medium">
                          Calendar Email (Optional Override)
                        </Label>
                        <div className="flex gap-2">
                          <Input
                            id="google-calendar-user"
                            type="email"
                            placeholder="e.g., calendar@company.com"
                            value={googleCalendarUserId}
                            onChange={(e) => setGoogleCalendarUserId(e.target.value)}
                            className="flex-1"
                          />
                          <Button
                            onClick={() => saveGoogleCalendarUserMutation.mutate(googleCalendarUserId)}
                            disabled={saveGoogleCalendarUserMutation.isPending || !googleCalendarUserId.trim()}
                          >
                            {saveGoogleCalendarUserMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            Save
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Optionally specify a different Google Calendar email to check for availability.
                          Leave blank to use the connected account's primary calendar.
                        </p>
                        {googleCalendarUserId && (
                          <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
                            <p className="text-xs text-blue-700 dark:text-blue-300">
                              <strong>Calendar Override:</strong> <code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">{googleCalendarUserId}</code>
                            </p>
                          </div>
                        )}
                      </div>

                      {googleTestResult && (
                        <div className={`p-4 rounded-lg border ${googleTestResult.success ? 'bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800'}`}>
                          <div className="flex items-start gap-3">
                            {googleTestResult.success ? (
                              <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
                            ) : (
                              <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5" />
                            )}
                            <div className="space-y-2">
                              <p className={`text-sm font-medium ${googleTestResult.success ? 'text-green-900 dark:text-green-100' : 'text-red-900 dark:text-red-100'}`}>
                                {googleTestResult.message}
                              </p>
                              {googleTestResult.success && googleTestResult.slots && googleTestResult.slots.length > 0 && (
                                <div className="mt-2">
                                  <p className="text-xs text-green-700 dark:text-green-300 font-medium mb-1">Available Slots:</p>
                                  <ul className="text-xs text-green-600 dark:text-green-400 space-y-0.5">
                                    {googleTestResult.slots.map((slot, index) => (
                                      <li key={index} className="flex items-center gap-1">
                                        <span className="w-1 h-1 bg-green-500 rounded-full"></span>
                                        {slot}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      <DialogFooter className="gap-2 sm:gap-0">
                        <Button
                          variant="outline"
                          onClick={() => testGoogleConnectionMutation.mutate()}
                          disabled={testGoogleConnectionMutation.isPending}
                        >
                          {testGoogleConnectionMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                          Test Connection
                        </Button>
                      </DialogFooter>
                    </>
                  ) : (
                    <>
                      {/* Not Connected State */}
                      <div className="p-4 rounded-lg border border-dashed border-muted-foreground/25 bg-muted/20">
                        <div className="flex flex-col items-center justify-center py-6 space-y-4">
                          <div className="p-3 rounded-full bg-muted">
                            <CalendarDays className="h-8 w-8 text-muted-foreground" />
                          </div>
                          <div className="text-center space-y-1">
                            <p className="text-sm font-medium">Connect Your Google Calendar</p>
                            <p className="text-xs text-muted-foreground">
                              Sign in with your Google account to enable calendar availability checking and automatic Google Meet links.
                            </p>
                          </div>
                          <Button
                            onClick={() => connectGoogleCalendarMutation.mutate()}
                            disabled={connectGoogleCalendarMutation.isPending}
                            className="mt-2"
                          >
                            {connectGoogleCalendarMutation.isPending ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
                                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                              </svg>
                            )}
                            Connect with Google
                          </Button>
                        </div>
                      </div>

                      <div className="rounded-lg border p-4 space-y-2">
                        <h4 className="text-sm font-medium">What you get:</h4>
                        <ul className="text-xs text-muted-foreground space-y-1">
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="h-3 w-3 text-green-500" />
                            Real-time calendar availability checking
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="h-3 w-3 text-green-500" />
                            Automatic Google Meet link generation
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="h-3 w-3 text-green-500" />
                            Calendar events created on your behalf
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="h-3 w-3 text-green-500" />
                            Meeting invites sent to attendees
                          </li>
                        </ul>
                      </div>
                    </>
                  )}
                </>
              )}
            </TabsContent>

            <TabsContent value="team" className="space-y-6 py-4">
              {/* Owner / Admin Status */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-primary" />
                  <Label className="text-base font-semibold">Calendar Owner</Label>
                </div>

                {/* Show connected calendar owner status */}
                {(googleCalendarStatus?.connected || microsoftCalendarStatus?.connected) ? (
                  <div className="p-4 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
                      <div className="space-y-1 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-green-900 dark:text-green-100">
                            Owner Calendar Connected
                          </p>
                          <Badge variant="outline" className="text-green-700 border-green-300 dark:text-green-300 dark:border-green-700">
                            Verified
                          </Badge>
                        </div>
                        <p className="text-xs text-green-700 dark:text-green-300">
                          {calendarProvider === 'google'
                            ? googleCalendarStatus?.email
                            : microsoftCalendarStatus?.email}
                        </p>
                        <p className="text-xs text-green-600 dark:text-green-400">
                          Provider: {calendarProvider === 'google' ? 'Google Calendar' : 'Microsoft Outlook'}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 rounded-lg border border-dashed border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5" />
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
                          No Calendar Connected
                        </p>
                        <p className="text-xs text-amber-700 dark:text-amber-300">
                          Connect your calendar in the Calendar tab first to enable team management.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Approved Domains */}
              {approvedDomains.length > 0 && (
                <>
                  <Separator />
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Link2 className="h-4 w-4 text-primary" />
                      <Label className="text-base font-semibold">Approved Domains</Label>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Users from these domains can book meetings using your calendar without individual OAuth.
                    </p>
                    <div className="space-y-2">
                      {approvedDomains.map((domain) => (
                        <div key={domain.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border">
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary">{domain.domain}</Badge>
                            <span className="text-xs text-muted-foreground">
                              via {domain.provider === 'microsoft' ? 'Microsoft' : 'Google'}
                            </span>
                          </div>
                          {domain.isActive && (
                            <Badge variant="outline" className="text-green-600 border-green-300">
                              Active
                            </Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              <Separator />

              {/* How Team Members Connect */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-primary" />
                  <Label className="text-base font-semibold">Team Member Access</Label>
                </div>
                <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                        Domain-Based Access Enabled
                      </p>
                      <p className="text-xs text-blue-700 dark:text-blue-300">
                        Team members from your approved domain can log in and connect their calendars automatically.
                        They simply need to:
                      </p>
                      <ol className="text-xs text-blue-700 dark:text-blue-300 list-decimal list-inside space-y-1">
                        <li>Log in with their email and password</li>
                        <li>Go to the Appointments page</li>
                        <li>Click to connect their Google or Microsoft calendar</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Add Team Member Form */}
              {(googleCalendarStatus?.connected || microsoftCalendarStatus?.connected) && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <UserPlus className="h-4 w-4 text-primary" />
                    <Label className="text-base font-semibold">Add Team Member</Label>
                  </div>
                  <div className="p-4 rounded-lg border bg-muted/20">
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label htmlFor="inviteEmail" className="text-xs">Email Address</Label>
                          <Input
                            id="inviteEmail"
                            type="email"
                            placeholder="team@company.com"
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                            className="h-9"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="inviteName" className="text-xs">Name (Optional)</Label>
                          <Input
                            id="inviteName"
                            type="text"
                            placeholder="John Doe"
                            value={inviteName}
                            onChange={(e) => setInviteName(e.target.value)}
                            className="h-9"
                          />
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="usesAdminCalendar"
                          checked={usesAdminCalendar}
                          onCheckedChange={setUsesAdminCalendar}
                        />
                        <Label htmlFor="usesAdminCalendar" className="text-sm font-normal cursor-pointer">
                          Use my calendar (no mailbox connection required)
                        </Label>
                      </div>
                      {usesAdminCalendar && (
                        <p className="text-xs text-muted-foreground bg-blue-50 dark:bg-blue-950/30 p-2 rounded">
                          This team member will be added as a host without connecting their own mailbox. All meetings will be scheduled on your calendar.
                        </p>
                      )}
                      <Button
                        onClick={() => inviteTeamMemberMutation.mutate({
                          email: inviteEmail,
                          name: inviteName || undefined,
                          usesAdminCalendar
                        })}
                        disabled={!inviteEmail || inviteTeamMemberMutation.isPending}
                        className="w-full"
                        size="sm"
                      >
                        {inviteTeamMemberMutation.isPending ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <UserPlus className="h-4 w-4 mr-2" />
                        )}
                        {usesAdminCalendar ? 'Add as Host' : 'Send Invitation'}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              <Separator />

              {/* Team Members List */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-primary" />
                    <Label className="text-base font-semibold">Team Members</Label>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => refetchTeamMembers()}
                    className="h-8 px-2"
                  >
                    <RefreshCw className="h-3 w-3" />
                  </Button>
                </div>

                {teamMembers.length === 0 ? (
                  <div className="p-6 rounded-lg border border-dashed text-center">
                    <Users className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">
                      No team members added yet.
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Add team members above. They can either connect their own calendar or use your calendar.
                    </p>
                  </div>
                ) : (
                  <ScrollArea className="max-h-[300px]">
                    <div className="space-y-2">
                      {teamMembers.map((member) => (
                        <div
                          key={member.id}
                          className={`p-3 rounded-lg border ${
                            member.status === 'connected'
                              ? 'bg-green-50/50 dark:bg-green-950/20 border-green-200 dark:border-green-800'
                              : member.status === 'expired'
                              ? 'bg-red-50/50 dark:bg-red-950/20 border-red-200 dark:border-red-800'
                              : 'bg-amber-50/50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-sm">
                                  {member.name || member.email.split('@')[0]}
                                </span>
                                <Badge
                                  variant={
                                    member.status === 'connected'
                                      ? 'default'
                                      : member.status === 'expired'
                                      ? 'destructive'
                                      : 'secondary'
                                  }
                                  className="text-[10px] px-1.5"
                                >
                                  {member.status === 'connected' ? 'Connected' : member.status === 'expired' ? 'Expired' : 'Pending'}
                                </Badge>
                                {member.usesAdminCalendar && (
                                  <Badge variant="outline" className="text-[10px] px-1.5 border-blue-300 text-blue-600">
                                    Uses Admin Calendar
                                  </Badge>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground">{member.email}</p>
                              {member.status === 'connected' && member.calendarEmail && (
                                <p className="text-xs text-green-600 dark:text-green-400">
                                  {member.usesAdminCalendar ? 'Host Calendar:' : 'Calendar:'} {member.calendarEmail}
                                </p>
                              )}
                              {member.status === 'pending' && member.inviteExpiresAt && (
                                <p className="text-xs text-amber-600 dark:text-amber-400">
                                  Expires: {formatDate(member.inviteExpiresAt, 'MMM d, yyyy')}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              {member.status === 'pending' && member.inviteToken && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => copyInviteLink(member)}
                                    className="h-7 w-7 p-0"
                                    title="Copy invite link"
                                  >
                                    {copiedInviteLink === member.id ? (
                                      <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                                    ) : (
                                      <Copy className="h-3.5 w-3.5" />
                                    )}
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => resendInviteMutation.mutate(member.id)}
                                    disabled={resendInviteMutation.isPending}
                                    className="h-7 w-7 p-0"
                                    title="Resend invitation"
                                  >
                                    <RefreshCw className={`h-3.5 w-3.5 ${resendInviteMutation.isPending ? 'animate-spin' : ''}`} />
                                  </Button>
                                </>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeTeamMemberMutation.mutate(member.id)}
                                disabled={removeTeamMemberMutation.isPending}
                                className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                title="Remove team member"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </div>

              {/* Info box about how it works */}
              <div className="rounded-lg border p-4 bg-muted/30">
                <h4 className="text-sm font-medium mb-2">How Team Calendars Work</h4>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li className="flex items-start gap-2">
                    <span className="text-primary font-medium">1.</span>
                    <span>Admin connects their calendar (domain is auto-approved).</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary font-medium">2.</span>
                    <span>Add team members using the form above.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary font-medium">3.</span>
                    <span>Team members can either connect their own calendar OR use admin's calendar.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary font-medium">4.</span>
                    <span>AI agents can schedule meetings with any team member as the host.</span>
                  </li>
                </ul>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
      <Dialog open={selectedDate !== null} onOpenChange={(open) => { if (!open) setSelectedDate(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedDate && format(selectedDate, "MMMM d, yyyy")}
            </DialogTitle>
            <DialogDescription>
              {selectedDate && (() => {
                const dayApts = getAppointmentsForDate(selectedDate);
                return dayApts.length > 0
                  ? `${dayApts.length} appointment${dayApts.length > 1 ? "s" : ""}`
                  : t("appointments.noAppointments");
              })()}
            </DialogDescription>
          </DialogHeader>

          {selectedDate && (() => {
            const dayAppointments = getAppointmentsForDate(selectedDate)
              .sort((a, b) => new Date(a.scheduledFor).getTime() - new Date(b.scheduledFor).getTime());

            if (dayAppointments.length === 0) {
              return (
                <div className="flex flex-col items-center justify-center py-12">
                  <CalendarIcon className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">{t("appointments.noAppointments")}</h3>
                  <p className="text-sm text-muted-foreground text-center max-w-md">
                    {t("appointments.noAppointmentsDescription")}
                  </p>
                </div>
              );
            }

            return (
              <ScrollArea className="max-h-[60vh]">
                <div className="space-y-3 pr-4">
                  {dayAppointments.map((apt) => (
                    <Card key={apt.id} className="hover-elevate">
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <User className="h-4 w-4 text-muted-foreground" />
                              <div className="font-semibold">{apt.contactName}</div>
                              <Badge variant={apt.status === "confirmed" ? "default" : "secondary"}>
                                {apt.status}
                              </Badge>
                              {apt.metadata?.phoneDiscrepancy && (
                                <Badge variant="outline" className="text-amber-600 border-amber-300 dark:text-amber-400 dark:border-amber-700">
                                  <AlertCircle className="h-3 w-3 mr-1" />
                                  Phone mismatch
                                </Badge>
                              )}
                            </div>
                            <div className="text-sm text-muted-foreground space-y-0.5">
                              <div className="flex items-center gap-1">
                                <span className="font-medium text-foreground">{apt.contactPhone}</span>
                                {apt.contactEmail && <span>• {apt.contactEmail}</span>}
                              </div>
                              {apt.metadata?.phoneDiscrepancy && apt.metadata.aiCollectedPhone && (
                                <div className="text-xs text-amber-600 dark:text-amber-400">
                                  AI heard: {apt.metadata.aiCollectedPhone}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-medium">
                              {formatDate(apt.scheduledFor, "h:mm a")}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {t("appointments.durationMinutes", { count: apt.duration })}
                            </div>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          {apt.serviceName && (
                            <div>
                              <span className="text-muted-foreground">{t("appointments.details.service")}: </span>
                              <span className="font-medium">{apt.serviceName}</span>
                            </div>
                          )}
                        </div>
                        {apt.notes && (
                          <div className="mt-3 p-2 bg-muted/50 rounded-md text-sm">
                            <span className="text-muted-foreground">{t("appointments.details.notes")}: </span>
                            {apt.notes}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </ScrollArea>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
